import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import qs from 'qs';
import { createInfiniteQuery } from 'react-query-kit';

import { HTTP_HEADERS } from '@/constants';
import { getInjector } from '@/services/angularjs-bridge';
import { filterEmptyParams, restUrl } from '@/services/utils';

import { BusinessPartner, establishedContacts, partnersNextCursor } from './businessPartner';

interface PartnersParams {
    limit: number;
    keywords?: string;
}

interface PartnersResponse {
    data: BusinessPartner[];
    cursor?: string;
}

export interface ContactPublication {
    id: number;
    creationDate?: number | string;
    taskStatus?: string;
    publishedItemsCount?: number;
    totalItemsCount?: number;
}

const invalidateContactCache = async (queryClient: ReturnType<typeof useQueryClient>) => {
    try {
        const service = getInjector().get('ContactCacheService');
        service.invalidateContacts();
    } catch {
        // The Angular shell is absent in tests.
    }
    await queryClient.invalidateQueries({ queryKey: ['contacts'] });
    await queryClient.invalidateQueries({ queryKey: ['organizations', 'search'] });
};

export const useBusinessPartners = createInfiniteQuery<
    PartnersResponse,
    PartnersParams,
    Error,
    string | undefined
>({
    queryKey: ['contacts', 'partners'],
    fetcher: async (variables, { pageParam }) => {
        const response = await axios.get<BusinessPartner[]>(restUrl('contacts/search'), {
            params: {
                limit: variables.limit,
                keywords: variables.keywords,
                sortby: 'name',
            },
            headers: pageParam ? { [HTTP_HEADERS.X_CURSOR]: pageParam } : undefined,
            paramsSerializer: (params) => qs.stringify(filterEmptyParams(params)),
        });
        const rows = response.data ?? [];
        return {
            data: establishedContacts(rows),
            cursor: partnersNextCursor(rows.length, variables.limit, response.headers[HTTP_HEADERS.X_CURSOR]),
        };
    },
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: undefined,
});

export const useContactPublications = (organizationId: number | undefined, enabled: boolean) =>
    useQuery({
        queryKey: ['contacts', 'publications', organizationId],
        enabled: enabled && organizationId != null,
        queryFn: async () => {
            const response = await axios.get<ContactPublication[]>(restUrl('publications'), {
                params: { recipient: organizationId },
            });
            const publications = response.data ?? [];
            return [...publications].sort((left, right) => {
                const leftDate = new Date(left.creationDate ?? 0).getTime();
                const rightDate = new Date(right.creationDate ?? 0).getTime();
                return rightDate - leftDate;
            });
        },
    });

export const useCreateContact = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (contact: BusinessPartner) => axios.post(restUrl('contacts'), contact),
        onSuccess: () => invalidateContactCache(queryClient),
    });
};

export const useUpdateContact = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (contact: BusinessPartner) => axios.put(restUrl('contacts'), contact),
        onSuccess: () => invalidateContactCache(queryClient),
    });
};

export const useDeleteContact = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (contactId: number) => axios.delete(restUrl(`contacts/${contactId}`)),
        onSuccess: () => invalidateContactCache(queryClient),
    });
};

export const useRepublishPublication = () =>
    useMutation({
        mutationFn: (publicationId: number) => axios.post(
            restUrl(`publications/republish/${publicationId}`),
            null,
            { params: { calledFromFrontend: true } },
        ),
    });

export const loginAsManagingUser = (organizationId: number) =>
    axios.get(restUrl(`contacts/managingUserLogin/${organizationId}`));
